<script lang="ts">
    import vm from '@cwd/Journeys/Journeys.viewmodel';

    import { api } from '@cwd/lib/main';
    import { dateFormat, deepClone } from '@cwd/shared/utils';
    import HumanTime from '@shared/utils/HumanTime/HumanTime';
    import cronstrue from 'cronstrue/i18n';
    import { onMounted, reactive } from 'vue';

    import { RecurringEditorState, TaskSchedule, TaskScheduleController } from './CronProperties';

    import FluidButton from '@shared/components/FluidButton.vue';

    // utils
    import CronEditor from './CronEditor.vue';

    const toHumanTime = new HumanTime({ locale: 'sv' });

    const component = {
        name: 'RecurringEditor',
    };
    export default {
        name: component.name,
    };
</script>

<script setup lang="ts">
    const props = withDefaults(
        defineProps<{
            start?: Date | string | null;
            ownerId: string;
            ownerName?: string;
            steps: any;
            scheduleCallback?: Func;
            handleSchedule: Func;
            objectType?: string;
            resendToExistingRecipients?: boolean;
            scheduleController: TaskScheduleController;
            disabled?: boolean;
        }>(),
        {
            disabled: false,
            objectType: 'SendIssue',
            ownerName: 'Untitled',
            scheduleCallback: () => console.warn(`Schedule method not defined in ${component.name}`),
        }
    );

    const { editor, activeJourney, account, toast } = vm,
        { events } = editor;

    const emit = defineEmits<{
        (ev: 'update:start', value: Date | string | null): void;
    }>();

    onMounted(async () => {
        if (activeJourney.scheduleController?.startingStep && activeJourney.steps[activeJourney.scheduleController?.startingStep])
            await loadCurrentSchedules([activeJourney.steps[activeJourney.scheduleController?.startingStep].data.IssueId!]);
    });

    const state = reactive<RecurringEditorState>({
        showEditor: false,
        displayInactiveTasks: false,
        cronExpression: '',
        nextDates: [],
        schedules: [],
        selectedTaskSchedule: {},
        objectId: '',
        objectType: '',
        resendToExistingRecipients: false,
    });

    const updateResendProperty = (newValue: any) => {
        // api.post('/Issues/' + props.objectId + '/ResendToExistingRecipients', { '': newValue })
    };

    const getTaskSchedule = (schedule?: TaskSchedule): TaskSchedule => {
        const {
            TaskType = 'SendIssue',
            ObjectId = '',
            TaskId = '',
            Scheduled = props.start,
            CronExpression = '',
            TimeZone = props.scheduleController.TimeZone,
            TaskStatus = 'Scheduled',
        } = schedule!;

        return {
            TaskId: TaskId,
            ObjectId: ObjectId,
            CronExpression: CronExpression,
            Scheduled: Scheduled || null,
            TaskType: TaskType,
            IsRecurring: true,
            TaskStatus: TaskStatus,
            TimeZone: TimeZone,
        };
    };

    const loadTaskSchedule = (objectId: string) => api.get(`/Scheduling/${objectId}/Schedules`);

    const loadCurrentSchedules = (issueIds: string[]) => {
        const promises: Promise<any>[] = [];

        for (let id of issueIds) promises.push(loadTaskSchedule(id));

        return new Promise((res: any, rej: any) => {
            Promise.all(promises)
                .then((data: any) => {
                    state.schedules = data.flat();

                    res(data);
                })
                .catch((err: any) => {
                    console.error('Failed fetching Schedules:', err);
                    rej(err);
                });
        });
    };

    const loadStepSchedules = (objectIds: string[]) => {
        return api
            .post(`/Scheduling/Schedules`, objectIds)
            .then((res: any) => {
                console.log(res);
            })
            .catch((reason: any) => {
                toast.emit('note', {
                    pos: 'top-center',
                    type: 'error',
                    message: {
                        head: `There were problems fetching step schedules.`,
                        body:
                            'An error occured. <br>' +
                            '<span class="fw700" style="color: red">' +
                            reason.status +
                            '</span> <span class="fw700">' +
                            reason.statusText +
                            '</span>',
                        break: true,
                    },
                });
            });
    };

    /** delay is always in days, check for placement of '?'
     * to add to either days of month or days of week */
    const addDelayToCron = (expression: string, delay: number) => {
        let split_ex = expression.split(' ');

        if (/\?/g.test(split_ex[3])) {
            31 % delay;
        }
    };

    const setScheduleProps = (schedule: TaskSchedule) => {
        props.scheduleController.Scheduled = schedule.Scheduled!;
        props.scheduleController.TaskStatus = schedule.TaskStatus!;
        props.scheduleController.IsRecurring = schedule.IsRecurring!;
        props.scheduleController.CronExpression = schedule.CronExpression!;
        props.scheduleController.TimeZone = schedule.TimeZone!;
    };

    const saveSchedule = (schedule: TaskSchedule & { [k: string]: any }) => {
        const scheduled = state.nextDates[0];
        schedule.IsRecurring = true;
        schedule.TaskStatus = 'Scheduled';

        const steps = Object.values(activeJourney.steps).map((step: any) => {
            let data: {
                [k: string]: any;
                Schedule: TaskSchedule;
            } = deepClone(step.data);

            data.Name = activeJourney.Title + ' - ' + data.Name;
            data.SerializedExpression = JSON.stringify({
                SegmentId: data.SegmentId,
                Name: '[JS] - ' + data.Name,
                Description: $Resources.TaskCreatedByLbl + ' journey: ' + props.ownerName,
                SegmentDefinition: data.SegmentDefinition,
            });
            data.Schedule = deepClone<TaskSchedule>(schedule);
            data.Schedule.CronExpression = activeJourney.scheduleController.CronExpression;
            data.Schedule.TimeZone = activeJourney.scheduleController.TimeZone;
            data.Schedule.ObjectId = data.IssueId;
            data.Schedule.TaskId = data.TaskId;
            data.Schedule.Scheduled = activeJourney.scheduleController.Scheduled;
            data.NodeId = step.nodeId;
            data.Delay = step.delay;

            delete data.SegmentDefinition;
            return data;
        });

        api.post(`/journeys/${props.ownerId}/Publish`, steps)
            .then((response: any) => {
                props.scheduleController.startingStep = activeJourney.startingStep;

                for (let i = 0; i < response.length; i++) {
                    if (props.scheduleController.startingStep === response[i].NodeId) setScheduleProps(response[i].Schedule);

                    props.scheduleController.activeTasks[response[i].TaskId] = response[i];

                    account.listings.addListing('/issues/listing', response[i].IssueListing);
                    account.listings.addListing('/segments', response[i].SegmentListing);

                    activeJourney.ownedObjects.segments[response[i].SegmentListing.SegmentId] ||= response[i].SegmentListing.SegmentId;
                    activeJourney.ownedObjects.tasks[response[i].TaskId] ||= response[i].TaskId;
                    activeJourney.ownedObjects.issues[response[i].IssueId] ||= response[i].IssueId;
                }

                props.scheduleCallback(response, loadStepSchedules);
            })
            .catch((reason: any) => {
                Object.assign(activeJourney, editor.history.cache.safeguard);

                let errorMessage = reason.message != null ? JSON.parse(reason.message) : 'Unknown';

                // TODO: update toast (emittable) to support presets with position and type
                // TODO: also add html snippets?
                toast.emit('note', {
                    pos: 'top-center',
                    type: 'error',
                    message: {
                        head: `Couldn't publish journey.`,
                        body:
                            'There was an error during publishing. <br>' +
                            '<span class="fw700" style="color: red">' +
                            reason.statusText +
                            '</span> <span class="fw700">' +
                            errorMessage.Message +
                            '</span>',
                        break: true,
                    },
                });
            });
    };

    const saveSelectedSchedule = async (schedule: TaskSchedule = state.selectedTaskSchedule) => {
        await setState('save');
        return saveSchedule(schedule as TaskSchedule);
    };

    events.addListener('re_saveselectedschedule', () => saveSelectedSchedule(state.selectedTaskSchedule));

    // TODO: reschedule
    const cache: any = {
        schedule: {},
    };
    const setState = async (action: string | null = null, schedule?: TaskSchedule) => {
        // complete reset
        if (action === 'reset') {
            emit('update:start', '');
            state.selectedTaskSchedule = {};
            state.showEditor = true;
        }

        // current state options
        if (action === 'edit') {
            state.selectedTaskSchedule = getTaskSchedule(schedule);
            cache.schedule = state.selectedTaskSchedule;
            emit('update:start', (state.selectedTaskSchedule?.Scheduled as Date) || '');

            state.showEditor = true;
        }

        // empty template schedule
        if (!action) {
            state.selectedTaskSchedule = getTaskSchedule();

            state.showEditor = true;
        }

        // close and revert to cache
        if (action === 'close') {
            emit('update:start', (cache.schedule?.Scheduled as Date) || '');
            state.selectedTaskSchedule = cache.schedule;

            state.showEditor = false;
        }

        // update on close
        if (action === 'save') {
            await updateCurrentDate();

            state.showEditor = false;
        }
    };

    events.addListener('re_croneditor', setState);

    const updateCurrentDate = async () => {
        if (!state.nextDates || Object.keys(state.nextDates).length === 0) {
            await api.get(
                '/CronExpressions/GetFutureDates?cronExpression=' +
                    encodeURIComponent(activeJourney.scheduleController.CronExpression) +
                    '&timeZone=' +
                    activeJourney.scheduleController.TimeZone +
                    '&count=5&start=' +
                    encodeURIComponent(dateFormat('yyyy-mm-dd', activeJourney.scheduleController.Scheduled)),
                null,
                state.nextDates
            );
        } else {
            if (state.selectedTaskSchedule) {
                state.selectedTaskSchedule.Scheduled = state.nextDates[0];
            } else {
                state.selectedTaskSchedule = { Scheduled: state.nextDates[0] };
            }
        }

        emit('update:start', state.selectedTaskSchedule?.Scheduled as any);
    };

    const disable = (schedule: TaskSchedule) => {
        let taskIds = Object.values(props.scheduleController.activeTasks).map((task: any) => task.TaskId);

        api.post(`/journeys/Schedule/Stop`, taskIds).then((res: any) => {
            props.scheduleController.TaskStatus = 'Aborted';

            for (let i = 0; i < state.schedules.length; i++) state.schedules[i].TaskStatus = 'Aborted';

            props.scheduleCallback(null, loadStepSchedules);

            state.selectedTaskSchedule = {
                TimeZone: '',
            };
            state.showEditor = false;
        });
    };

    events.addListener('re_disableactiveschedule', () => disable(state.selectedTaskSchedule));

    let cronstrueOptions = { locale: appconfig.lang.loc };
</script>

<template>
    <!--
	<h3 class="h4">{{ $Resources.GuideDashboardScheduledHeading }}</h3>
    -->

    <div class="form-group" v-show="!state.showEditor">
        <FluidButton
            class="btn btn-default"
            :disabled="disabled"
            :class="state.showEditor && 'idle'"
            :text="!activeJourney.scheduleController.Scheduled ? $Resources.SetScheduleLabel : $Resources.EditScheduleLabel"
            @click="handleSchedule" />
    </div>

    <div class="form-group">
        <span class="label-look d-block">{{ $Resources.RecuringStartDateLabel }}:</span>
        <strong>{{ toHumanTime.parseAbs(activeJourney.scheduleController.Scheduled) || $Resources.SetScheduleLabel }}</strong>
    </div>

    <div class="form-group" v-if="activeJourney.scheduleController.CronExpression">
        <span class="label-look d-block">{{ $Resources.RecuringCronExpressionLabel }}:</span>
        <strong>{{ cronstrue.toString(activeJourney.scheduleController.CronExpression, cronstrueOptions) }}</strong>
    </div>

    <template v-if="state.showEditor">
        <CronEditor
            v-model:cronExpression="activeJourney.scheduleController.CronExpression"
            v-model:timeZone="activeJourney.scheduleController.TimeZone"
            v-model:selectedStart="activeJourney.scheduleController.Scheduled"
            v-model:nextDates="state.nextDates" />
    </template>
</template>

<style>
    .btn.idle {
        pointer-events: none;
    }
</style>
